import React, { useEffect, useMemo, useState } from "react";
import { Product } from "../../Payment/types/payment";
import PaymentActions from "./PaymentActions";
import { Divider, Checkbox } from "@mui/material";
import { User } from "lucide-react";
import { usePaymentCalculations } from "../hooks/usePaymentCalculations";
import PaymentInputField from "./PaymentInputField";
import CommonButtons from "src/shared/components/CommonButtons";
import { LedgerDetails } from "src/shared/services/interfacev2";

type SplitMethod = "Cash" | "Card";
interface SplitEntry {
  id: number;
  method: SplitMethod;
  amount: string;
}

const MAX_SPLITS = 4;
const INITIAL_SPLITS: SplitEntry[] = [{ id: 1, method: "Cash", amount: "" }];

interface PaymentSummaryProps {
  products: Product[];
  customer: LedgerDetails | null;
  paymentMethod: "Cash" | "Buy on Credit" | "Card" | "Split";
  receivedAmount: number;
  showBalance: boolean;
  currentTime: { hours: string; minutes: string; day: string; month: string; year: string };
  onAmountChange: (value: number) => void;
  onToggleBalance: () => void;
  roundAmount: (value: any) => string;
  onCancel: () => void;
  onComplete: () => void;
  isLoading?: boolean;
  backHome: (value: string) => void;
  buttonLoading?: boolean;
  deliveryFeeEnabled: boolean;
  // Optional: breakdown of each split payment, e.g. [{ method: "Cash", amount: 100 }, { method: "Card", amount: 10 }]
  onSplitChange?: (splits: { method: string; amount: number }[]) => void;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  products,
  customer,
  paymentMethod,
  receivedAmount,
  showBalance,
  currentTime,
  onAmountChange,
  onToggleBalance,
  roundAmount,
  onCancel,
  onComplete,
  isLoading = false,
  backHome,
  buttonLoading = false,
  deliveryFeeEnabled,
  onSplitChange,
}) => {
  const { orderAmount, discount, deliveryFee, finalAmount, outstandingBalance, totalPayable, remainingAmount } = usePaymentCalculations(products, customer, showBalance, receivedAmount);

  const isSplit = paymentMethod === "Split";
  const [splits, setSplits] = useState<SplitEntry[]>(INITIAL_SPLITS);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    if (value > totalPayable) value = totalPayable;
    onAmountChange(value);
  };

  const orderPriceData = Number(outstandingBalance) + Number(orderAmount);

  // ---------- Split helpers ----------
  const splitTotal = useMemo(() => splits.reduce((sum, s) => sum + Number(s.amount || 0), 0), [splits]);

  const splitRemaining = Math.max(Number(totalPayable) - splitTotal, 0);

  const addSplit = () => {
    if (splits.length >= MAX_SPLITS || splitRemaining <= 0) return;
    setSplits((prev) => [
      ...prev,
      {
        id: Date.now(),
        method: prev.some((s) => s.method === "Cash") ? "Card" : "Cash",
        amount: splitRemaining.toFixed(2), // prefill remaining
      },
    ]);
  };

  const removeSplit = (id: number) => setSplits((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));

  const updateSplitMethod = (id: number, method: SplitMethod) => setSplits((prev) => prev.map((s) => (s.id === id ? { ...s, method } : s)));

  // Amount is clamped so the sum of all rows never exceeds Total Payable
  const updateSplitAmount = (id: number, raw: string) => {
    if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;

    setSplits((prev) => {
      const othersTotal = prev.filter((s) => s.id !== id).reduce((sum, s) => sum + Number(s.amount || 0), 0);
      const max = Math.max(Number(totalPayable) - othersTotal, 0);
      const value = raw === "" ? "" : Number(raw) > max ? max.toFixed(2) : raw;
      return prev.map((s) => (s.id === id ? { ...s, amount: value } : s));
    });
  };

  const hasSplitAmount = splits.some((split) => split.amount.trim() !== "" && Number(split.amount) > 0);

  // Reset rows whenever the payment type changes
  useEffect(() => {
    setSplits(INITIAL_SPLITS);
  }, [paymentMethod]);

  // Received Amount = sum of split rows
  useEffect(() => {
    if (isSplit) {
      onAmountChange(splitTotal);
    }
  }, [isSplit, splitTotal]);

  // Send breakdown to parent
  useEffect(() => {
    if (isSplit && onSplitChange) {
      onSplitChange(splits.filter((s) => Number(s.amount) > 0).map((s) => ({ method: s.method, amount: Number(s.amount) })));
    }
  }, [isSplit, onSplitChange, splits]);

  return (
    <div className="text-left py-2">
      <div className="flex justify-end mb-2 -mt-4">
        <CommonButtons label="Add Product" onClick={() => backHome("false")} className="w-36" />
      </div>
      <div className="flex items-center gap-3 bg-gray-100 p-3 px-5 mb-2 rounded-md">
        <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
          <User className="text-gray-600" size={22} />
        </div>
        <div className="flex flex-col text-sm">
          <span className="font-semibold">
            Customer: {(customer?.customerDetails.firstName ?? "") + (customer?.customerDetails.middleName ?? "") + (customer?.customerDetails.lastName ?? "") || "N/A"}
          </span>
          <span className="text-gray-600">Payment: {paymentMethod}</span>
          <span className="text-gray-500 text-xs">
            Date: {currentTime.year}-{currentTime.month}-{currentTime.day} | {currentTime.hours}:{currentTime.minutes}
          </span>
        </div>
      </div>

      {/* Order Summary */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-700 mb-1">Order Summary</h3>
        <Divider className="mb-1.5" />
        {paymentMethod !== "Buy on Credit" && (
          <div className="flex items-center justify-between text-sm">
            <div className="">
              <Checkbox onChange={onToggleBalance} checked={showBalance} size="small" disabled={Number(outstandingBalance) < 0} />
              <span className="font-medium">Outstanding Balance</span>
            </div>
            <span className="font-medium">{roundAmount(outstandingBalance)} QAR</span>
          </div>
        )}
        <div className="space-y-2 text-sm">
          {paymentMethod === "Buy on Credit" && (
            <div className="flex mt-2 justify-between">
              <span className="px-10">Outstanding Balance</span>
              <span className="font-medium">{roundAmount(outstandingBalance)} QAR</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="px-10">Order Amount</span>
            <span className="font-medium">{roundAmount(orderAmount)} QAR</span>
          </div>
          <div className="flex justify-between">
            <span className="px-10">Discount</span>
            <span>- {roundAmount(discount)} QAR</span>
          </div>
          {deliveryFeeEnabled && (
            <div className="flex justify-between">
              <span className="px-10">Delivery Fee</span>
              <span>{roundAmount(deliveryFee)} QAR</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="px-10">Final Amount</span>
            <span>{roundAmount(finalAmount)} QAR</span>
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div className="mb-2">
        <h3 className="font-semibold text-gray-700 mb-1">Payment Status</h3>
        <Divider className="mb-2" />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="px-10 mt-2">Total Payable</span>
            <span className="font-medium mt-2">{roundAmount(totalPayable)} QAR</span>
          </div>

          {/* Split rows */}
          {isSplit && (
            <div className="p-3 bg-gray-50 rounded-md border border-gray-200 space-y-2">
              <span className="font-semibold text-gray-700">Split Payment</span>

              {splits.map((s, index) => (
                <div key={s.id} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-4">{index + 1}.</span>

                  <select
                    value={s.method}
                    onChange={(e) => updateSplitMethod(s.id, e.target.value as SplitMethod)}
                    className="px-2 py-1 border border-gray-300 rounded-md bg-white text-gray-700 focus:ring-2 focus:ring-green-400 focus:outline-none"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                  </select>

                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={s.amount}
                    onChange={(e) => updateSplitAmount(s.id, e.target.value)}
                    disabled={orderPriceData <= 0}
                    className="flex-1 min-w-0 px-2 py-1 text-right font-semibold border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-green-400 focus:outline-none"
                  />
                  <span className="font-bold text-xs">QAR</span>

                  {splits.length > 1 && (
                    <button type="button" onClick={() => removeSplit(s.id)} className="w-6 h-6 rounded-full border border-red-300 text-red-500 hover:bg-red-50 text-xs" aria-label="Remove split">
                      ✕
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addSplit}
                disabled={splits.length >= MAX_SPLITS || splitRemaining <= 0}
                className="w-full py-1 rounded-md border border-dashed border-blue-400 text-blue-600 font-medium hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                + Add Payment
              </button>

              <div className="flex justify-between text-xs text-gray-600">
                <span>Split total: {roundAmount(splitTotal)} QAR</span>
                <span>Remaining: {roundAmount(splitRemaining)} QAR</span>
              </div>
            </div>
          )}

          {/* Received Amount: editable only for Cash/Card, read-only for Credit and Split */}
          {paymentMethod === "Buy on Credit" ? (
            <PaymentInputField label="Received Amount" value={0} roundAmount={roundAmount} isEditable={false} />
          ) : (
            <PaymentInputField
              label="Received Amount"
              value={receivedAmount}
              onChange={handleInputChange}
              roundAmount={roundAmount}
              isEditable={isSplit ? false : orderPriceData <= 0 ? false : true}
            />
          )}
          <PaymentInputField label="Remaining Amount" value={remainingAmount} roundAmount={roundAmount} isEditable={false} />
        </div>
      </div>

      {/* Actions */}
      <Divider className="my-2" />
      <div className="flex justify-center -mt-1 -mb-3">
        <PaymentActions
          onCancel={onCancel}
          onComplete={onComplete}
          isLoading={isLoading}
          backHomepay={() => {}}
          receivedAmount={receivedAmount}
          paymentMethod={paymentMethod}
          buttonLoading={buttonLoading}
          orderPriceData={orderPriceData}
          hasSplitAmount={hasSplitAmount}
        />
      </div>
    </div>
  );
};

export default PaymentSummary;
